import { FormEvent, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft, AlertCircle } from "lucide-react";
import { useAuth } from "../../services/auth";

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username, password);
      navigate("/admin");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed. Please verify credentials.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="w-full max-w-md">
        {/* Return to live site */}
        <div className="mb-4 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={13} />
            <span>Return to ZimSmart Public Edition</span>
          </Link>
        </div>

        <form className="login-panel" onSubmit={submit}>
          <div className="flex items-center gap-3">
            <div className="login-mark">
              <Lock size={22} />
            </div>
            <div>
              <div className="text-[10px] font-extrabold tracking-widest text-[#C99A2E] uppercase">
                Newsroom CMS Portal
              </div>
              <h1 className="text-xl font-serif font-black text-slate-900 leading-tight">
                ZimSmart Editorial
              </h1>
            </div>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed -mt-1">
            Sign in with authorized newsroom credentials to manage publications, journalism taxonomies, and advertising campaigns.
          </p>

          {error && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-3.5">
            <label>
              Email or Username
              <input
                name="emailOrUsername"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. editor or admin@zimsmart.co.zw"
                autoComplete="username"
                required
              />
            </label>

            <label>
              Secret Password
              <input
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </label>
          </div>

          <button className="button" type="submit" disabled={loading}>
            {loading ? "Verifying Credentials..." : "Sign In to Newsroom"}
          </button>

          <a href="mailto:it@zimsmart.co.zw" className="text-center text-xs text-slate-400 hover:text-[#C99A2E]">
            Contact Newsroom Desk / IT Support
          </a>
        </form>
      </div>
    </div>
  );
}
